import { Button, Tag, type Column } from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';
import type { AgentPeriodRow, AgentRow, PeriodRow, PlanRow } from './types.ts';
import {
  PERIOD_STATUS_LABEL,
  PLAN_KIND_LABEL,
  PLAN_TIER_LABEL,
  SETTLEMENT_LABEL,
  STATUS_LABEL,
  TIER_LABEL,
  actionLabel,
  bandSentence,
  carrySentence,
  periodTone,
  retainerSentence,
  stamp,
  statusTone,
  subLine,
  tierTone,
} from './vocabulary.tsx';

/* ── Columns ───────────────────────────────────────────────────────────────
   Factories, because the Agent cell and the Action button both open the drawer
   and therefore both need the opener. The design opens the drawer from the
   Agent cell specifically rather than from the whole row, so the cell carries a
   real button instead of the row carrying role="button" with a button nested
   inside it. */

export function agentColumns(onOpen: (agent: AgentRow) => void): Column<AgentRow>[] {
  return [
    {
      key: 'agent',
      header: 'Agent',
      render: (a) => (
        <>
          <Button variant="ghost" small onClick={() => onOpen(a)}>
            {a.name}
          </Button>
          <div className="text-subtle" style={subLine}>
            {a.ref} · {a.brand}
            {/* Whether this partner can SIGN IN, on the row rather than only in
                the drawer. Stated as an ABSENCE — "no portal login" — because
                that is what it is, and because until the API grew a way to
                create one it was the state of every agent this console had ever
                made. Nothing is printed once they can sign in: that is the
                resting state, and a badge on every row would train the eye past
                the two that need one. The word "portal" is in both phrases so
                neither can be read as the agent's OWN status, which uses
                "invited" for something else entirely. */}
            {a.portal_status === 'NONE' && ' · no portal login'}
            {a.portal_status === 'INVITED' && ' · portal invite not accepted'}
          </div>
        </>
      ),
    },
    {
      key: 'parent',
      header: 'Parent',
      // Dropped on a phone: the hierarchy rail above already names the branch
      // in view, the tier chip says which level the row sits on, and the drawer
      // prints the parent in its subtitle. Nothing is lost, only repeated.
      secondary: true,
      render: (a) =>
        a.parent_ref ? (
          <>
            <span className="tabular">{a.parent_ref}</span>
            <div className="text-subtle" style={subLine}>
              {a.parent_name}
            </div>
          </>
        ) : (
          <span className="text-subtle">top of tree</span>
        ),
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (a) => <Tag tone={tierTone(a.tier)}>{TIER_LABEL[a.tier] ?? a.tier}</Tag>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <>
          <Tag tone={statusTone(a.status)}>{STATUS_LABEL[a.status] ?? a.status}</Tag>
          {a.status === 'SUSPENDED' && (
            <div className="text-subtle" style={subLine}>
              accrues, not paid
            </div>
          )}
        </>
      ),
    },
    {
      key: 'players',
      header: 'Players',
      numeric: true,
      // Live attribution only (agent_player.valid_to IS NULL), so a player who
      // moved between agents counts once, under whoever holds them now.
      render: (a) => <span className="tabular">{formatCount(Number(a.players))}</span>,
    },
    {
      key: 'ggr',
      header: 'GGR 30d',
      numeric: true,
      render: (a) => <span className="tabular">{formatPesosCompact(BigInt(a.ggr_30d))}</span>,
    },
    {
      key: 'commission',
      header: 'Commission',
      numeric: true,
      // The design rounds this to whole pesos. It is a payable, and the ledger
      // settles it to the centavo, so it prints in full — a figure an agent
      // will reconcile against their statement should not be pre-rounded.
      render: (a) => <span className="tabular">{formatPesos(BigInt(a.commission))}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      render: (a) => (
        <Button small onClick={() => onOpen(a)} aria-label={`${actionLabel(a.status)} ${a.name}`}>
          {actionLabel(a.status)}
        </Button>
      ),
    },
  ];
}

/**
 * The plan book.
 *
 * Every column here used to be a hedge. "Plan" printed a derived name with
 * "derived name — no plan record exists" underneath it; "Settlement" borrowed
 * the platform's measured cadence because the plan had none of its own; and
 * "Negative carry-over" said "no term stored", because nothing stored one.
 * All four are plan columns now, and the only hedge left is the one that is
 * still true: a plan's settlement RULE is compared against the periods the
 * platform actually issues, and the mismatch is reported rather than hidden.
 */
export function planColumns(args: {
  measuredSettlement: string | null;
  canWrite: boolean;
  archiving: string | null;
  onArchive: (plan: PlanRow) => void;
}): Column<PlanRow>[] {
  const { measuredSettlement, canWrite, archiving, onArchive } = args;
  return [
    {
      key: 'plan',
      header: 'Plan',
      render: (p) => (
        <>
          {p.name}
          <div className="text-subtle" style={subLine}>
            {p.ref} · {p.brand}
            {p.status === 'ARCHIVED' ? ' · archived' : ''}
          </div>
        </>
      ),
    },
    {
      key: 'applies',
      header: 'Applies to',
      render: (p) => (
        <>
          <span className="text-muted">{PLAN_TIER_LABEL[p.tier] ?? p.tier}</span>
          <div className="text-subtle" style={subLine}>
            {PLAN_KIND_LABEL[p.kind] ?? p.kind}
          </div>
        </>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      render: (p) => {
        const retainer = retainerSentence(p.retainer_amount, p.settlement);
        return (
          <>
            <span className="tabular">{bandSentence(p.bands)}</span>
            {retainer && (
              <div className="text-subtle" style={subLine}>
                plus a {retainer}
              </div>
            )}
            {p.bands.length > 1 && (
              <div className="text-subtle" style={subLine}>
                whole amount, not marginal
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'settlement',
      header: 'Settlement',
      secondary: true,
      render: (p) => (
        <>
          {SETTLEMENT_LABEL[p.settlement] ?? p.settlement}
          {measuredSettlement && measuredSettlement !== p.settlement && (
            <div className="text-subtle" style={subLine}>
              periods run {(SETTLEMENT_LABEL[measuredSettlement] ?? '').toLowerCase()}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'carry',
      header: 'Negative carry-over',
      secondary: true,
      render: (p) => <span className="text-muted">{carrySentence(p.carry_over_months)}</span>,
    },
    {
      key: 'agents',
      header: 'Agents',
      numeric: true,
      render: (p) => <span className="tabular">{formatCount(Number(p.agents))}</span>,
    },
    ...(canWrite
      ? [
          {
            key: 'archive',
            header: 'Withdraw',
            numeric: true,
            render: (p: PlanRow) =>
              p.status === 'ARCHIVED' ? (
                <span className="text-subtle">withdrawn</span>
              ) : (
                <Button
                  small
                  disabled={archiving === p.id}
                  onClick={() => onArchive(p)}
                  aria-label={`Withdraw ${p.name}`}
                >
                  {archiving === p.id ? 'Withdrawing…' : 'Withdraw'}
                </Button>
              ),
          } satisfies Column<PlanRow>,
        ]
      : []),
  ];
}

export const periodColumns: Column<PeriodRow>[] = [
  {
    key: 'period',
    header: 'Period',
    render: (p) => (
      <>
        <span className="tabular">{p.ref}</span>
        <div className="text-subtle" style={subLine}>
          {stamp(p.period_start)} – {stamp(p.period_end)}
        </div>
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => (
      <Tag tone={periodTone(p.status)}>{PERIOD_STATUS_LABEL[p.status] ?? p.status}</Tag>
    ),
  },
  {
    key: 'agents',
    header: 'Agents',
    secondary: true,
    numeric: true,
    render: (p) => <span className="tabular">{formatCount(Number(p.agent_count))}</span>,
  },
  {
    key: 'net',
    header: 'Net revenue',
    secondary: true,
    numeric: true,
    render: (p) => <span className="tabular">{formatPesosCompact(BigInt(p.net_revenue))}</span>,
  },
  {
    key: 'commission',
    header: 'Commission',
    numeric: true,
    render: (p) => <span className="tabular">{formatPesos(BigInt(p.commission))}</span>,
  },
  {
    key: 'held',
    header: 'Held',
    numeric: true,
    // agent_period_result.held is how "a suspended agent accrues but is not
    // paid" is actually stored, so it is worth a column of its own.
    render: (p) =>
      BigInt(p.held) === 0n ? (
        <span className="text-subtle">none</span>
      ) : (
        <span className="tabular">{formatPesos(BigInt(p.held))}</span>
      ),
  },
];

/**
 * One agent's own period history — the rows the drawer said "nothing exposes …
 * for a single agent".
 *
 * Carry is its own column because it is the figure that explains a commission
 * an agent will otherwise query: a period whose net revenue was ₱3.6M and whose
 * commission was calculated on ₱3.2M is not an error, it is last period's loss
 * being offset, and the drawer has to be able to say so.
 */
export const agentPeriodColumns: Column<AgentPeriodRow>[] = [
  {
    key: 'period',
    header: 'Period',
    render: (p) => (
      <>
        <span className="tabular">{p.ref}</span>
        <div className="text-subtle" style={subLine}>
          {stamp(p.period_start)} – {stamp(p.period_end)}
        </div>
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => (
      <>
        <Tag tone={periodTone(p.status)}>{PERIOD_STATUS_LABEL[p.status] ?? p.status}</Tag>
        {p.held && (
          <div className="text-subtle" style={subLine}>
            held
          </div>
        )}
      </>
    ),
  },
  {
    key: 'net',
    header: 'Net revenue',
    numeric: true,
    render: (p) => <span className="tabular">{formatPesosCompact(BigInt(p.net_revenue))}</span>,
  },
  {
    key: 'rate',
    header: 'Rate',
    secondary: true,
    numeric: true,
    // The rate the period was CALCULATED at, frozen on the row — not the
    // agent's rate today. A plan change is effective-dated, so those two
    // legitimately differ and printing today's rate over a settled period
    // would misstate what was paid.
    render: (p) => <span className="tabular">{formatRate(Number(p.rate_bp))}</span>,
  },
  {
    key: 'carry',
    header: 'Carry in / out',
    secondary: true,
    numeric: true,
    render: (p) =>
      BigInt(p.carry_in) === 0n && BigInt(p.carry_out) === 0n ? (
        <span className="text-subtle">none</span>
      ) : (
        <span className="tabular">
          {formatPesos(BigInt(p.carry_in))} / {formatPesos(BigInt(p.carry_out))}
        </span>
      ),
  },
  {
    key: 'commission',
    header: 'Commission',
    numeric: true,
    render: (p) => <span className="tabular">{formatPesos(BigInt(p.net_commission))}</span>,
  },
  {
    key: 'players',
    header: 'Players',
    secondary: true,
    numeric: true,
    render: (p) => <span className="tabular">{formatCount(Number(p.player_count))}</span>,
  },
];
